import { useCallback, useEffect, useRef } from "react";
import EditIcon from "@/assets/icons/edit-01.svg?react";
import CheckIcon from "@/assets/icons/check.svg?react";
import TrashIcon from "@/assets/icons/trash.svg?react";
import { cn } from "@/lib/utils";
import { useFormContext } from "react-hook-form";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { parseEnum } from "@/lib/enums.util";
import { GetPreferenceResponse } from "@/types/api/api-types";
import { CotationUpdateBoFormData } from "@/types/forms/bo/cotationUpdateBoForm.form";
import { DeliveryContentData } from "@/types/forms/home/cotationDeliveryForm.form";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { Separator } from "@/components/ui/separator";
import { volumesOptions } from "@/constants/options/volumes.option";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { useWineColorOptions } from "@/hooks/useEnumOptions";
import { useWineColorLabels } from "@/hooks/useEnumLabels";
import { useTranslation } from "react-i18next";

export const WineListElem = ({
  item,
  index,
  remove,
  error,
  preference,
  hasScannedInvoice,
  deliveryContentLength,
}: {
  item: DeliveryContentData;
  index: number;
  remove: (index: number) => void;
  error: boolean;
  preference: GetPreferenceResponse | undefined;
  hasScannedInvoice?: boolean;
  deliveryContentLength: number;
}) => {
  const wineColorOptions = useWineColorOptions();
  const wineColorLabels = useWineColorLabels();
  const { t } = useTranslation("boExpeditions");
  const { control, setValue, watch, trigger } = useFormContext<CotationUpdateBoFormData>();
  const inputContainerRef = useRef<HTMLDivElement>(null);

  // Watch the isEditing field directly from form state
  const isEditing = watch(`deliveryContent.${index}.isEditing`);
  const itemWatch = watch(`deliveryContent.${index}`);

  const handleEdit = useCallback(
    async (status: boolean) => {
      if (status === false) {
        const triggerRet = await trigger(`deliveryContent.${index}`);
        if (!triggerRet) return;
      }

      setValue(`deliveryContent.${index}.isEditing`, status);
    },
    [index, setValue, trigger],
  );

  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        // Check if the focused element is inside our input container
        if (
          inputContainerRef.current &&
          inputContainerRef.current.contains(document.activeElement)
        ) {
          handleEdit(false);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isEditing, handleEdit]);

  const handlePreferenceWineChange = (e: any) => {
    if (!e) {
      return;
    }
    const winePreference = preference?.winePreference.find(
      (winePreference) => winePreference.id === e,
    );

    if (!winePreference) return;
    // Use setValue for each field to avoid overwriting other values
    setValue(`deliveryContent.${index}.wineName`, winePreference.wineName);
    setValue(`deliveryContent.${index}.year`, winePreference.year);
    setValue(`deliveryContent.${index}.alcoholPercent`, winePreference.alcoholPercent);
    setValue(`deliveryContent.${index}.volume`, winePreference.volume);
    setValue(
      `deliveryContent.${index}.color`,
      parseEnum(WineColorEnum, winePreference.color) ?? WineColorEnum.ROUGE,
    );
    setValue(`deliveryContent.${index}.price`, winePreference.unitPrice);
    trigger([
      `deliveryContent.${index}.wineName`,
      `deliveryContent.${index}.year`,
      `deliveryContent.${index}.alcoholPercent`,
      `deliveryContent.${index}.volume`,
      `deliveryContent.${index}.color`,
      `deliveryContent.${index}.price`,
    ]);
  };

  const handleRemoveProduct = () => {
    if (deliveryContentLength <= 1) return;
    remove(index);
  };

  if (hasScannedInvoice) {
    return (
      <div
        className={`flex items-center justify-center gap-4 rounded-lg border bg-gray-50 p-4 lg:justify-between ${error ? "border-red-500" : ""}`}
      >
        <div className="flex w-full flex-row justify-between gap-4">
          <div />
          <div />
          {isEditing ? (
            <div ref={inputContainerRef} className="flex flex-1 flex-wrap gap-4">
              <ControlledSelectV2
                label={t("pendingShipmentsWineItem.volume")}
                control={control}
                name={`deliveryContent.${index}.volume`}
                options={volumesOptions.map((volume) => ({
                  label: volume.label,
                  value: volume.value.toString(),
                }))}
                wrapperClassName="min-w-0 flex-1"
              />
              <ControlledUnifiedInput
                label={t("pendingShipmentsWineItem.quantityShort")}
                control={control}
                name={`deliveryContent.${index}.quantity`}
                inputType="integer"
                placeholder={t("pendingShipmentsWineItem.quantity")}
                className="flex-1"
              />
              <ControlledUnifiedInput
                label={t("pendingShipmentsWineItem.unitPriceShort")}
                control={control}
                name={`deliveryContent.${index}.price`}
                inputType="float"
                maxDecimals={4}
                placeholder={t("pendingShipmentsWineItem.unitPrice")}
                className="flex-1"
              />
            </div>
          ) : (
            <>
              <DisplayCaracteristic
                name={t("pendingShipmentsWineItem.volumePerBottle")}
                value={item.volume + " L"}
              />
              <DisplayCaracteristic
                name={t("pendingShipmentsWineItem.quantity")}
                value={item.quantity}
              />
              <DisplayCaracteristic
                name={t("pendingShipmentsWineItem.unitPrice")}
                value={item.price + " €"}
              />
            </>
          )}
          <div />
          <div />
        </div>
        <div className="flex w-fit gap-4">
          <button
            type="button"
            onClick={() => handleEdit(!isEditing)}
            className="rounded-full px-2 py-2 hover:bg-gray-200 disabled:pointer-events-none disabled:text-gray-500 disabled:hover:bg-transparent"
          >
            {isEditing ? <CheckIcon /> : <EditIcon />}
          </button>
          <button
            type="button"
            onClick={handleRemoveProduct}
            className="rounded-full px-2 py-2 hover:bg-gray-200"
          >
            <TrashIcon color="#DC2626" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center justify-center gap-4 rounded-lg border bg-gray-50 p-2 sm:p-4 lg:justify-between ${error ? "border-red-500" : ""}`}
    >
      {isEditing ? (
        <div ref={inputContainerRef} className="flex flex-1 flex-col gap-4">
          <SelectV2
            placeholder={t("pendingShipmentsWineItem.savedWines")}
            clearable={true}
            options={
              preference?.winePreference?.map((winePreference) => ({
                value: winePreference.id,
                label:
                  winePreference.wineName +
                  " - " +
                  winePreference.volume +
                  " L" +
                  " - " +
                  winePreference.year,
              })) || []
            }
            onChange={(e) => handlePreferenceWineChange(e)}
            contentClassName="h-[56px] sm:w-[300px]"
            wrapperClassName="w-fit"
          />
          <Separator />
          <div ref={inputContainerRef} className="hidden flex-wrap items-start gap-2 md:flex">
            <ControlledUnifiedInput
              label={t("pendingShipmentsWineItem.wineName")}
              control={control}
              name={`deliveryContent.${index}.wineName`}
              wrapperClassName="min-w-[130px]"
              placeholder={t("pendingShipmentsWineItem.wineName")}
            />
            <ControlledUnifiedInput
              label={t("pendingShipmentsWineItem.vintage")}
              control={control}
              name={`deliveryContent.${index}.year`}
              inputType="integer"
              wrapperClassName="w-[100px]"
              placeholder={t("pendingShipmentsWineItem.vintage")}
            />
            <ControlledUnifiedInput
              label={t("pendingShipmentsWineItem.alcoholPercent")}
              control={control}
              inputType="float"
              maxDecimals={2}
              name={`deliveryContent.${index}.alcoholPercent`}
              wrapperClassName="w-[70px]"
              placeholder={t("pendingShipmentsWineItem.alcoholPercent")}
            />
            <ControlledSelectV2
              label={t("pendingShipmentsWineItem.volume")}
              control={control}
              name={`deliveryContent.${index}.volume`}
              options={volumesOptions.map((volume) => ({
                label: volume.label,
                value: volume.value.toString(),
              }))}
              placeholder={t("pendingShipmentsWineItem.volume")}
              wrapperClassName="w-[100px]"
              defaultValue={undefined as unknown as number}
            />
            <ControlledSelectV2
              options={wineColorOptions}
              label={t("pendingShipmentsWineItem.color")}
              placeholder=""
              control={control}
              name={`deliveryContent.${index}.color`}
              wrapperClassName="w-[100px]"
            />
            <ControlledUnifiedInput
              label={t("pendingShipmentsWineItem.quantityShort")}
              control={control}
              name={`deliveryContent.${index}.quantity`}
              inputType="integer"
              wrapperClassName="w-[90px]"
              placeholder={t("pendingShipmentsWineItem.quantity")}
              defaultValue={""}
            />
            <ControlledUnifiedInput
              label={t("pendingShipmentsWineItem.unitPriceShort")}
              control={control}
              name={`deliveryContent.${index}.price`}
              inputType="float"
              maxDecimals={4}
              wrapperClassName="w-[90px]"
              placeholder={t("pendingShipmentsWineItem.unitPrice")}
            />
            <div className="w-fit items-center justify-center gap-2 pt-3">
              <button
                type="button"
                onClick={() => handleEdit(!isEditing)}
                className="rounded-full px-1 py-1 hover:bg-gray-200 disabled:pointer-events-none disabled:text-gray-500 disabled:hover:bg-transparent"
              >
                {isEditing ? <CheckIcon /> : <EditIcon />}
              </button>
              <button
                type="button"
                onClick={handleRemoveProduct}
                className={cn("rounded-full px-1 py-1 hover:bg-gray-200", {
                  hidden: deliveryContentLength <= 1,
                })}
              >
                <TrashIcon color="#DC2626" />
              </button>
            </div>
          </div>
          <div ref={inputContainerRef} className="flex flex-1 flex-col gap-2 md:hidden">
            <div className="flex flex-row gap-2">
              <ControlledUnifiedInput
                label={t("pendingShipmentsWineItem.wineName")}
                control={control}
                name={`deliveryContent.${index}.wineName`}
                className="flex-2/3 md:min-w-[130px]"
              />
              <ControlledUnifiedInput
                label={t("pendingShipmentsWineItem.vintage")}
                control={control}
                name={`deliveryContent.${index}.year`}
                inputType="integer"
                className="min-w-[100px] flex-1/3 md:w-[100px]"
              />
            </div>
            <div className="flex flex-row gap-2">
              <ControlledUnifiedInput
                label={t("pendingShipmentsWineItem.alcoholPercent")}
                control={control}
                inputType="float"
                maxDecimals={2}
                name={`deliveryContent.${index}.alcoholPercent`}
                className="w-full"
              />
              <ControlledSelectV2
                label={t("pendingShipmentsWineItem.volume")}
                control={control}
                name={`deliveryContent.${index}.volume`}
                options={volumesOptions.map((volume) => ({
                  label: volume.label,
                  value: volume.value.toString(),
                }))}
                contentClassName="w-full"
                wrapperClassName="w-full"
                placeholder={t("pendingShipmentsWineItem.volume")}
              />
            </div>
            <div className="flex flex-row gap-2">
              <ControlledSelectV2
                options={wineColorOptions}
                label={t("pendingShipmentsWineItem.color")}
                placeholder=""
                control={control}
                name={`deliveryContent.${index}.color`}
                wrapperClassName="w-full"
                contentClassName="w-full"
              />
              <ControlledUnifiedInput
                label={t("pendingShipmentsWineItem.quantity")}
                control={control}
                name={`deliveryContent.${index}.quantity`}
                inputType="integer"
                className="w-full"
              />
            </div>
            <div className="flex flex-row gap-2">
              <ControlledUnifiedInput
                label={t("pendingShipmentsWineItem.unitPrice")}
                control={control}
                name={`deliveryContent.${index}.price`}
                inputType="float"
                maxDecimals={4}
                className="flex-1"
              />
              <div className="flex w-fit gap-2">
                <button
                  type="button"
                  onClick={() => handleEdit(!isEditing)}
                  className="rounded-full px-1 py-1 hover:bg-gray-200 disabled:pointer-events-none disabled:text-gray-500 disabled:hover:bg-transparent"
                >
                  {isEditing ? <CheckIcon /> : <EditIcon />}
                </button>
                <button
                  type="button"
                  onClick={handleRemoveProduct}
                  className={cn("rounded-full px-1 py-1 hover:bg-gray-200", {
                    hidden: deliveryContentLength <= 1,
                  })}
                >
                  <TrashIcon color="#DC2626" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-1 flex-wrap justify-between gap-4">
          <DisplayCaracteristic
            name={t("pendingShipmentsWineItem.wineName")}
            value={itemWatch.wineName}
            className="max-w-[200px]"
          />
          <DisplayCaracteristic
            name={t("pendingShipmentsWineItem.vintage")}
            value={itemWatch.year || "-"}
          />
          <DisplayCaracteristic
            name={t("pendingShipmentsWineItem.alcoholPercent")}
            value={itemWatch.alcoholPercent ? itemWatch.alcoholPercent + "%" : ""}
          />
          <DisplayCaracteristic
            name={t("pendingShipmentsWineItem.volume")}
            value={isNaN(itemWatch.volume) ? "-" : itemWatch.volume + " L"}
          />
          <DisplayCaracteristic
            name={t("pendingShipmentsWineItem.color")}
            value={wineColorLabels[itemWatch.color as WineColorEnum] || itemWatch.color}
          />
          <DisplayCaracteristic
            name={t("pendingShipmentsWineItem.quantity")}
            value={itemWatch.quantity}
          />
          <DisplayCaracteristic
            name={t("pendingShipmentsWineItem.unitPrice")}
            value={!isNaN(itemWatch.price) ? itemWatch.price + " €" : ""}
          />
          <div className="flex w-fit gap-2 lg:hidden">
            <button
              type="button"
              onClick={() => handleEdit(!isEditing)}
              className="rounded-full px-1 py-1 hover:bg-gray-200 disabled:pointer-events-none disabled:text-gray-500 disabled:hover:bg-transparent"
            >
              {isEditing ? <CheckIcon /> : <EditIcon />}
            </button>
            <button
              type="button"
              onClick={handleRemoveProduct}
              className={cn("rounded-full px-1 py-1 hover:bg-gray-200", {
                hidden: deliveryContentLength <= 1,
              })}
            >
              <TrashIcon color="#DC2626" />
            </button>
          </div>
        </div>
      )}
      <div className={cn("w-fit gap-2", isEditing ? "hidden" : "hidden lg:flex")}>
        <button
          type="button"
          onClick={() => handleEdit(!isEditing)}
          className="rounded-full px-1 py-1 hover:bg-gray-200 disabled:pointer-events-none disabled:text-gray-500 disabled:hover:bg-transparent"
        >
          {isEditing ? <CheckIcon /> : <EditIcon />}
        </button>
        <button
          type="button"
          onClick={handleRemoveProduct}
          className={cn("rounded-full px-1 py-1 hover:bg-gray-200", {
            hidden: deliveryContentLength <= 1,
          })}
        >
          <TrashIcon color="#DC2626" />
        </button>
      </div>
    </div>
  );
};

export const DisplayCaracteristic = ({
  name,
  value,
  className = "",
}: {
  name: string;
  value: string | number;
  className?: string;
}) => {
  return (
    <div className={cn("items-left flex flex-col gap-2", className)}>
      <p className="text-xs font-medium text-gray-500">{name}</p>
      <p className="truncate text-sm font-medium text-gray-900">{value}</p>
    </div>
  );
};
